import { Badge, Card, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, statusVariant } from "@blog/ui";
import { Link } from "react-router-dom";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { buildRows } from "../lib/article-list";
import { formatDate } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Pages list (SPEC §40).
 *
 * The same shape as the article list, over `type=page`. Kept as its own route
 * because "articles" and "pages" are different things to an editor — one is a
 * stream, the other is a fixed set of destinations — and a combined list with a
 * type column is a list nobody scans.
 */
export function PageList() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const data = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listEntries(siteId, { type: "page", limit: 200 });
  }, [siteId]);

  if (siteLoading || data.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  const rows = data.data === null ? [] : buildRows(data.data.entries, data.data.localizations);

  return (
    <>
      <PageHeader
        title={t("pages.title")}
        description={t("articles.countDesc", { count: rows.length })}
      />

      {data.error === null ? null : <p className="mb-4 text-sm text-red-600">{data.error}</p>}

      {rows.length === 0 ? (
        <EmptyState
          message={t("pages.noPages")}
          hint={t("pages.emptyHint")}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.title")}</TableHead>
                <TableHead>{t("common.locale")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("common.path")}</TableHead>
                <TableHead>{t("common.updated")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.entry.id}:${row.localization.locale}`}>
                  <TableCell>
                    <Link
                      className="font-medium text-neutral-900 hover:underline"
                      to={`/articles/${row.entry.id}/${row.localization.locale}`}
                    >
                      {row.localization.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.localization.locale}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(row.localization.status)}>
                      {row.localization.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-neutral-400">
                    {row.localization.contentPath}
                  </TableCell>
                  <TableCell className="text-neutral-500">
                    {formatDate(row.localization.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
